import "./AddToBasketControl.scss";

import * as React from "react";

import PropTypes from "prop-types";
import { Button, Popover, OverlayTrigger } from "react-bootstrap";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";
import { FormattedMessage } from "react-intl";
import CartActions from "../../../actions/CartActions";
import {
    BasketAddTrackingData,
    GAAdditionalInfo,
    GAChangeBasketProduct,
    SaleCycleInfo,
    trackAddToBasket
} from "shared/src/tracking/BasketTrackingUtil";
import { saveRecentSearches } from "./RecentSearches";
import { AmountChooser } from "./AmountChooser";
import { PageType } from "shared/src/types/PageType";
import { ZeroResultType } from "shared/src/types/ZeroResultType";
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { SellingPriceDnrInfo, PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { MinOrderQuantityPanel } from "../../general/MinOrderQuantityPanel";
import { getShowsSummaryDnrInfo } from "shared/src/utils/articleUtil";
import { calculateAmountForBasket, calculateAmountToDisplay } from "shared/src/utils/BasketUtil";
import { PageComponent } from "shared/src/tracking/PageComponents";

type AddToBasketControlProps = {
    idForBasket: string;
    amountInBasket: number | undefined | null;
    priceInBasket: ShoppingCartPosition | undefined | null;
    isWeightArticle: boolean;
    weightPerPiece:
        | {
              unit: string;
              value: number;
          }
        | undefined
        | null;
    netPieceWeight: number | undefined | null;
    dnrInfo: SellingPriceDnrInfo | undefined | null;
    onlyEmployeeBuyable: boolean;
    deliveryMode: string | null;
    disabled: boolean;
    loggedIn: boolean;
    isTier12Customer: boolean;
    tabIndex?: number;
    minOrderQuantity?: number;
    priceAndPromotionInfo: PriceAndPromotionInfo;
    trackingData: {
        originEventId: string;
        inContainer: boolean | undefined | null;
        onList: boolean | undefined | null;
        recentlyOrdered: boolean | undefined | null;
        recentlyOrderedDateString: string | undefined | null;
        promotion: boolean | undefined | null;
        pageType: PageType;
        zeroResultType: ZeroResultType | undefined | null;
        bettyBundleId: string;
        trackingId: string;
        description: string;
        marketplace: boolean;
        offerId: string | undefined | null;
        articleFinalNetValue: number | null;
        currency: string;
        brandName: string;
        variantName: string;
        strokePrice: number | null;
        categoryPath: string;
        subsysNumber: string;
        mgbNumber: string;
        unitSize: string | null;
        weight: number | undefined | null;
        listPosition: number | undefined | null;
        listId: string | undefined | null;
        availability: string | undefined | null;
        ownBrand: string;
        loggedIn: boolean | undefined | null;
        url: string | undefined | null;
        imageSrc: string | undefined | null;
        ropoRank?: number | undefined | null;
        pageComponent?: PageComponent;
    };
    renderMinimally: boolean;
};

type State = {
    articleJustAdded: boolean;
    showWeightInformation: boolean;
    quantityInAmountChooser: number | undefined | null;
    userDidModifyAmount: boolean;
};

export class AddToBasketControl extends React.Component<AddToBasketControlProps, State> {
    static contextTypes = {
        intl: PropTypes.object.isRequired
    };

    static defaultProps = {
        renderMinimally: false
    };

    state: State = {
        articleJustAdded: false,
        showWeightInformation: false,
        quantityInAmountChooser: this.getAmountChooserPlaceholder(),
        userDidModifyAmount: false
    };

    trackBasketChange(value: number, currentValue: number) {
        const trackingData = this.props.trackingData;

        const productPrice = trackingData.articleFinalNetValue && trackingData.articleFinalNetValue.toString();

        const additionalInfo = {
            pageComponent: this.props.trackingData?.pageComponent,
            initial: !this.props.amountInBasket,
            inContainer: trackingData.inContainer,
            isPromo: trackingData.promotion,
            isOnList: trackingData.onList,
            isRecentlyOrdered: trackingData.recentlyOrdered,
            recentlyOrderedDateString: trackingData.recentlyOrderedDateString,
            marketplace: trackingData.marketplace,
            offerId: trackingData.offerId,
            brandName: trackingData.brandName,
            variantName: trackingData.variantName,
            categoryPath: trackingData.categoryPath,
            strokePrice: trackingData.strokePrice,
            subsysNumber: trackingData.subsysNumber,
            mgbNumber: trackingData.mgbNumber,
            weight: this.props.isWeightArticle ? trackingData.weight : null,
            unitSize: trackingData.unitSize,
            listPosition: trackingData.listPosition,
            listId: trackingData.listId,
            availability: trackingData.availability,
            amount: currentValue,
            ropoRank: trackingData?.ropoRank
        } as BasketAddTrackingData; // TODO: Align with type definition instead of forcing it on an incomplete tracking-dataset
        let gaPrice = trackingData.articleFinalNetValue;
        let gaStrokePrice = trackingData.strokePrice;
        let gaQuantity = currentValue;
        const isWeightPerPieceArticle = this.props.isWeightArticle && this.props.weightPerPiece;
        if (this.props.isWeightArticle && trackingData.unitSize?.toLowerCase() === "kg" && !isWeightPerPieceArticle) {
            // NOTE: Dealing with purely weighted products measured in "kg" here
            if (gaPrice !== null) {
                gaPrice /= 1000;
            }
            if (gaStrokePrice !== null) {
                gaStrokePrice /= 1000;
            }
            gaQuantity *= 1000;
        } else if (this.props.isWeightArticle && this.props.netPieceWeight && !isWeightPerPieceArticle) {
            // NOTE: Dealing with purely weighted products measured in other unit here
            if (gaPrice !== null) {
                gaPrice /= this.props.netPieceWeight;
            }
            if (gaStrokePrice !== null) {
                gaStrokePrice /= this.props.netPieceWeight;
            }
            gaQuantity *= this.props.netPieceWeight;
        }
        const gaProduct: GAChangeBasketProduct = {
            name: trackingData.description,
            id: trackingData.bettyBundleId,
            price: gaPrice?.toString(),
            brand: trackingData.brandName,
            variant: trackingData.variantName,
            quantity: gaQuantity,
            category: trackingData.categoryPath,
            metric3: gaStrokePrice ? gaStrokePrice.toString() : undefined,
            dimension62: trackingData.unitSize,
            dimension61: trackingData.mgbNumber,
            dimension42: trackingData.subsysNumber,
            metric6: this.props.weightPerPiece?.value || null
        };

        const gaAdditionalInfo: GAAdditionalInfo = {
            event: "addToCart",
            ecommerce: {
                currencyCode: trackingData.currency,
                add: {
                    products: [gaProduct]
                }
            }
        };

        const saleCycleInfo: SaleCycleInfo = {
            saleCycle: {
                loggedIn: trackingData.loggedIn,
                pageType: "Basket",
                items: [
                    {
                        q: currentValue,
                        p: {
                            name: trackingData.description,
                            id: trackingData.bettyBundleId, //unique identifier for article
                            costs: {
                                singleItem: productPrice,
                                previous: trackingData.strokePrice
                            },
                            url: trackingData.url,
                            images: { thumbnail: trackingData.imageSrc },
                            brand: trackingData.brandName,
                            description: trackingData.description,
                            size: { value: trackingData.unitSize },
                            inStock: !this.props.disabled && !(this.props.deliveryMode === "STORE"),
                            currency: trackingData.currency
                        }
                    }
                ]
            }
        };

        const showsSummaryDnrInfo = getShowsSummaryDnrInfo(this.props.priceAndPromotionInfo);

        trackAddToBasket(
            trackingData.originEventId,
            trackingData.pageType,
            trackingData.zeroResultType,
            trackingData.bettyBundleId,
            trackingData.trackingId,
            value,
            trackingData.articleFinalNetValue,
            trackingData.currency,
            additionalInfo,
            gaAdditionalInfo,
            saleCycleInfo,
            trackingData.ownBrand,
            showsSummaryDnrInfo
        );
    }

    getAmountChooserPlaceholder() {
        const basket = this.props.amountInBasket || 0;
        const minOrderQuantity = this.props.minOrderQuantity || 0;
        if (minOrderQuantity > basket) {
            return minOrderQuantity - basket;
        }
        return 1;
    }

    amountInBasketAndAmountChooserLowerThanMOQ() {
        const amountInChooser = this.getAmountInChooser();
        if (!this.props.minOrderQuantity) {
            return false;
        }
        return this.props.minOrderQuantity > amountInChooser! + (this.props.amountInBasket || 0);
    }

    addChosenAmountToBasket(event: React.SyntheticEvent<any> | undefined | null) {
        // If the added quantity is 1, there is no risk of undefined behaviour and the user is allowed to
        // click on the add to cart button several times.
        if (this.state.articleJustAdded && this.state.quantityInAmountChooser !== 1) {
            return;
        }
        // If the minumum order quantity is enabled and the amount is lower than the limit then don't add the value
        if (this.amountInBasketAndAmountChooserLowerThanMOQ()) {
            return;
        }
        if (event) {
            event.preventDefault();
        }

        this.setState({ articleJustAdded: true, showWeightInformation: true });
        // Reset state after some time
        window.setTimeout(() => this.setState({ articleJustAdded: false, quantityInAmountChooser: 1 }), 2000);
        window.setTimeout(() => {
            if (!this.state.articleJustAdded) {
                this.setState({ showWeightInformation: false });
            }
        }, 5000);

        const amount = this.state.quantityInAmountChooser || 0;
        const correctDelta = calculateAmountForBasket(amount, this.props.isWeightArticle, this.props.weightPerPiece);
        if (this.props.amountInBasket) {
            CartActions.changeAmountInCart(this.props.idForBasket, correctDelta);
        } else {
            CartActions.setItemInCart(this.props.idForBasket, correctDelta);
        }
        const correctTrackedValue = calculateAmountForBasket(
            amount + (this.props.amountInBasket || 0),
            this.props.isWeightArticle,
            this.props.weightPerPiece
        );
        this.trackBasketChange(correctTrackedValue, amount);
    }

    handleAmountUpdate = (amount: number | undefined | null) => {
        this.setState({ userDidModifyAmount: true, quantityInAmountChooser: amount });
    };

    handleEnterPress = () => {
        this.addChosenAmountToBasket(undefined);
    };

    getAmountInChooser() {
        return this.state.userDidModifyAmount ? this.state.quantityInAmountChooser : this.getAmountChooserPlaceholder();
    }

    renderMinOrderQuantityPanel() {
        if (!this.amountInBasketAndAmountChooserLowerThanMOQ()) return null;
        return (
            <div>
                <MinOrderQuantityPanel backgroundClassName={"background-notification-warning"} icon={"icon-warning"}>
                    <FormattedMessage
                        id="SHOPPING_CART_MINIMUM_QUANTITY_POPUP_MESSAGE"
                        values={{ count: this.props.minOrderQuantity }}
                    />
                </MinOrderQuantityPanel>
            </div>
        );
    }

    renderAddToBasketButton() {
        if (this.props.deliveryMode === "STORE") {
            const overlay = (
                <Popover id={"only-in-store-message-" + this.props.idForBasket}>
                    <FormattedMessage id="EX_POPOVER_ONLY_BUYABLE_IN_STORE_MESSAGE" />
                </Popover>
            );
            return (
                <div className="mfcss_card-article-2--actions-onlyinstore">
                    <OverlayTrigger trigger="hover" placement="top" overlay={overlay}>
                        <Button block>
                            <i className="icon-location icon-lg" />
                            <FormattedMessage id="EX_ONLY_AVAILABLE_IN_STORE_MESSAGE" />
                        </Button>
                    </OverlayTrigger>
                </div>
            );
        } else if (
            this.props.deliveryMode === "PICKUP" &&
            this.props.disabled &&
            (!this.props.loggedIn || this.props.isTier12Customer)
        ) {
            const overlay = (
                <Popover id={"pickup-message-" + this.props.idForBasket}>
                    <FormattedMessage id="EX_POPOVER_PICKUP_MESSAGE" />
                </Popover>
            );
            return (
                <div className="mfcss_card-article-2--actions-onlyinstore">
                    <OverlayTrigger trigger="hover" placement="top" overlay={overlay}>
                        <Button block>
                            <i className="icon-info icon-lg" />
                            <FormattedMessage id="EX_PICKUP_BUTTON_TEXT" />
                        </Button>
                    </OverlayTrigger>
                </div>
            );
        } else if (this.props.disabled) {
            return (
                <Button
                    block
                    disabled
                    className="pointer-events-none test-disabled-cart-button"
                    aria-label={this.context.intl.formatMessage({
                        id: "SD_ADD_TO_BASKET_BUTTON_ARIA_LABEL"
                    })}>
                    <i key="shoppingCartIconCart" className="icon-lg icon-cart no-margin" />
                </Button>
            );
        }
        const icon = this.state.articleJustAdded ? (
            <i key="shoppingCartIconCheck" className="icon-lg icon-check" />
        ) : (
            <i key="shoppingCartIconCart" className="icon-lg icon-cart" />
        );
        const shouldDisableAddButton = this.amountInBasketAndAmountChooserLowerThanMOQ();
        const className =
            "ex-add-to-basket-component add-to-basket-btn " +
            (this.props.onlyEmployeeBuyable
                ? " employee-only"
                : "" + (shouldDisableAddButton ? " pointer-events-none" : "")) +
            (this.props.renderMinimally ? " minimal" : "");
        return (
            <div className={"mfcss_card-article-2--actions-addtocart"}>
                <Button
                    onClick={(event: React.MouseEvent<Button, MouseEvent>) => {
                        this.addChosenAmountToBasket(event);
                        saveRecentSearches();
                    }}
                    bsStyle={this.props.onlyEmployeeBuyable ? "warning" : "primary"}
                    block
                    disabled={shouldDisableAddButton}
                    className={className}
                    aria-label={this.context.intl.formatMessage({
                        id: "SD_ADD_TO_BASKET_BUTTON_ARIA_LABEL"
                    })}>
                    <ReactCSSTransitionGroup
                        transitionName="animation-slideFromBottom"
                        transitionAppear
                        transitionAppearTimeout={0}
                        transitionEnterTimeout={500}
                        transitionLeaveTimeout={500}>
                        {icon}
                    </ReactCSSTransitionGroup>
                </Button>
            </div>
        );
    }

    renderAmountInBasket() {
        const amountInBasket = calculateAmountToDisplay(
            this.props.amountInBasket,
            this.props.isWeightArticle,
            this.props.weightPerPiece
        );
        let toRender;

        if (this.props.renderMinimally) {
            return null;
        }

        if (!amountInBasket) {
            toRender = <br />;
        } else {
            toRender = (
                <div>
                    <span className="icon-check" />
                    <strong>
                        <FormattedMessage id="EX_AMOUNT_IN_CART" values={{ amount: amountInBasket }} />
                    </strong>
                </div>
            );
        }
        return <div className="mfcss_card-article-2--info-amount-in-cart">{toRender}</div>;
    }

    render() {
        const buyable = this.props.deliveryMode !== "STORE" && !this.props.disabled;
        return (
            <React.Fragment>
                {buyable && !this.props.renderMinimally && (
                    <AmountChooser
                        onChange={this.handleAmountUpdate}
                        onEnter={this.handleEnterPress}
                        isWeightArticle={this.props.isWeightArticle}
                        weightPerPiece={this.props.weightPerPiece}
                        amountInBasket={this.props.amountInBasket}
                        amount={
                            this.state.userDidModifyAmount
                                ? this.state.quantityInAmountChooser
                                : this.getAmountChooserPlaceholder()
                        }
                        priceInBasket={this.props.priceInBasket}
                        dnrInfo={this.props.dnrInfo}
                        idForBasket={this.props.idForBasket}
                        disabled={this.props.disabled}
                        tabIndex={this.props.tabIndex}
                        minOrderQuantity={this.props.minOrderQuantity}
                        showWeightInformation={this.state.showWeightInformation}
                        priceAndPromotionInfo={this.props.priceAndPromotionInfo}
                    />
                )}

                {this.renderMinOrderQuantityPanel()}
                {this.renderAddToBasketButton()}
                {this.renderAmountInBasket()}
            </React.Fragment>
        );
    }
}
